/**
 * Turn a crash into text you can paste straight into a coding agent.
 *
 * The point is to remove the archaeology: an agent needs to know WHERE it broke
 * (url + route + bundle chunk), WHO hit it (role matters — most production
 * crashes are viewer/operator paths a developer never walks), WHAT broke (message
 * + both stacks; the React component stack is what names the component, while the
 * JS stack names the chunk), and WHICH build, because a stack referencing
 * `AdminFirmwareTab-ec85c9e1.js` is meaningless against different source.
 *
 * Everything is best-effort and never throws: this runs from inside an error
 * boundary, where the store, the router or even `navigator` may be part of the
 * problem.
 */

const APP_VERSION = (import.meta.env?.VITE_APP_VERSION as string) || 'unknown';

export interface ErrorReportInput {
  /** Error message ("TypeError: Cannot read properties of undefined…"). */
  message?: string | null;
  stack?: string | null;
  /** React's component stack — the one that names the component. */
  componentStack?: string | null;
  /** Which boundary caught it. */
  variant?: string | null;
  /** 'auto' (captured on catch) | 'user' (explicitly reported). */
  source?: string | null;
  url?: string | null;
  /** ISO timestamp; defaults to now. */
  when?: string | null;
  /** Who hit it. Role especially: it explains paths developers never take. */
  user?: { id?: number | null; email?: string | null; name?: string | null;
           role?: string | null } | null;
  userAgent?: string | null;
  appVersion?: string | null;
  /** Anything else worth pasting (feature flags, entity ids…). */
  extra?: Record<string, unknown> | null;
}

/**
 * Best-effort read of the signed-in user, from the redux-persist blob rather than
 * the live store.
 *
 * Deliberately not an import of the store: this runs from inside an error
 * boundary, so the store may be part of what broke, and importing it here would
 * add a cycle (store → slices → services → this). sessionStorage under the
 * `persist:auth` key needs nothing and cannot throw past the catch.
 */
function currentUser(): ErrorReportInput['user'] {
  try {
    const raw = window.sessionStorage.getItem('persist:auth');
    if (!raw) return null;
    // redux-persist stores each slice key as its own JSON string.
    const slice = JSON.parse(raw);
    const u = typeof slice.user === 'string' ? JSON.parse(slice.user) : slice.user;
    if (!u) return null;
    return {
      id: u.id, email: u.email, role: u.role,
      name: u.display_name || [u.name, u.surname].filter(Boolean).join(' ').trim() || null,
    };
  } catch {
    return null;
  }
}

/** The bundle chunk a stack points at — the fastest route to the source file. */
export function bundleFromStack(stack?: string | null): string | null {
  if (!stack) return null;
  const m = stack.match(/\/assets\/([A-Za-z0-9._-]+\.js)/);
  return m ? m[1] : null;
}

function routeOf(url?: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.search ? `${u.pathname} (${u.search.replace(/^\?/, '')})` : u.pathname;
  } catch {
    return url;
  }
}

function section(title: string, body: string | null): string {
  return body ? `\n## ${title}\n${body}\n` : '';
}

function fence(body?: string | null): string | null {
  const trimmed = (body || '').trim();
  return trimmed ? '```\n' + trimmed + '\n```' : null;
}

/**
 * Assemble a prompt-friendly report. Leads with an instruction so the paste is
 * actionable on its own, then the facts, most-identifying first.
 */
export function formatErrorForPrompt(input: ErrorReportInput): string {
  const url = input.url ?? (typeof window !== 'undefined' ? window.location.href : null);
  const when = input.when || new Date().toISOString();
  const user = input.user !== undefined ? input.user : currentUser();
  const ua = input.userAgent
    ?? (typeof navigator !== 'undefined' ? navigator.userAgent : null);
  const bundle = bundleFromStack(input.stack);

  const where = [
    url ? `- URL: ${url}` : null,
    routeOf(url) ? `- Route: ${routeOf(url)}` : null,
    input.variant ? `- Error boundary: ${input.variant}` : null,
    input.source ? `- Captured: ${input.source}` : null,
    `- When: ${when}`,
  ].filter(Boolean).join('\n');

  const who = user
    ? [
      `- User: ${user.name || user.email || `id ${user.id ?? '?'}`}`
      + (user.email && user.name ? ` <${user.email}>` : '')
      + (user.id != null ? ` (id ${user.id})` : ''),
      user.role ? `- Role: ${user.role}` : null,
    ].filter(Boolean).join('\n')
    : '- User: not signed in / unknown';

  const build = [
    `- App version: ${input.appVersion || APP_VERSION}`,
    bundle ? `- Bundle chunk: ${bundle} (source file is the component of the same name)` : null,
    ua ? `- Browser: ${ua}` : null,
    typeof window !== 'undefined' && window.innerWidth
      ? `- Viewport: ${window.innerWidth}×${window.innerHeight}` : null,
  ].filter(Boolean).join('\n');

  const extra = input.extra && Object.keys(input.extra).length
    ? fence(JSON.stringify(input.extra, null, 2))
    : null;

  return [
    'Frontend crash in the Saqia irrigation web app (React + TypeScript, MUI, '
    + 'Redux Toolkit). Find the component and the root cause from the stacks '
    + 'below, then fix it. The React component stack names the component; the JS '
    + 'stack names the built chunk it came from.',
    section('Where', where),
    section('Who hit it', who),
    section('Build', build),
    section('Error', input.message || '(no message)'),
    section('JS stack', fence(input.stack)),
    section('React component stack', fence(input.componentStack)),
    extra ? section('Extra context', extra) : '',
  ].join('').trim() + '\n';
}

/**
 * Copy text to the clipboard, with a fallback for insecure contexts (the
 * Clipboard API is unavailable over plain http, which is exactly where a dev
 * looking at a crash may be).
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
